import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { CTASection } from "../components/site/CTASection";
import { INSIGHTS, INSIGHT_CATEGORIES } from "../data/insights";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function Insights() {
  const [cat, setCat] = useState("All");
  const cats = ["All", ...INSIGHT_CATEGORIES.filter((c) => INSIGHTS.some((a) => a.category === c))];
  const list = cat === "All" ? INSIGHTS : INSIGHTS.filter((a) => a.category === cat);
  const featured = INSIGHTS[0];

  return (
    <div>
      <PageHero
        variant="dark"
        label="Insights"
        title="Independent Thinking on Markets, Industry & Technology"
        intro="MATRIVON Insights explores the questions that matter when companies make commercial and industrial decisions."
        crumbs={[{ label: "Insights" }]}
      />

      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          {/* Cross-category featured: Market Intelligence article */}
          <Link
            to="/market-intelligence/global-silicone-industry-2026"
            data-testid="insights-cross-global-silicone"
            className="group mb-12 flex flex-col justify-between gap-4 border border-brand-blue/30 bg-brand-blue/5 p-8 transition-colors hover:border-brand-blue sm:flex-row sm:items-center"
          >
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-red">Market Intelligence · Silicone Industry</span>
              <h2 className="mt-2 font-heading text-xl font-bold leading-tight text-brand-ink group-hover:text-brand-blue lg:text-2xl">Beyond the "Top Silicone Companies" Lists: The Global Silicone Industry in 2026</h2>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-brand-blue">Read report <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
          </Link>

          {/* Featured */}
          <Link
            to={`/insights/${featured.slug}`}
            data-testid="insight-featured"
            className="group mb-12 grid gap-8 border border-brand-line bg-brand-bg p-8 transition-colors hover:border-brand-blue lg:grid-cols-[1.6fr_1fr] lg:p-10"
          >
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-red">
                Featured · {featured.category}
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold leading-tight text-brand-ink group-hover:text-brand-blue lg:text-3xl">
                {featured.title}
              </h2>
              <p className="mt-4 text-[16px] leading-relaxed text-brand-muted">{featured.excerpt}</p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue">
                Read article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
            <div className="flex items-end text-sm text-brand-muted lg:justify-end">
              {fmtDate(featured.published)} · {featured.readMins} min read
            </div>
          </Link>

          {/* Filters */}
          <div className="mb-8 flex flex-wrap gap-2">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`insight-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`border px-4 py-2 text-sm font-medium transition-colors ${
                  cat === c
                    ? "border-brand-blue bg-brand-blue text-white"
                    : "border-brand-line bg-white text-brand-muted hover:border-brand-blue hover:text-brand-blue"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid gap-px border border-brand-line bg-brand-line md:grid-cols-2 lg:grid-cols-3">
            {list.map((a) => (
              <Link
                key={a.slug}
                to={`/insights/${a.slug}`}
                data-testid={`insight-card-${a.slug}`}
                className="group flex flex-col bg-white p-7 transition-colors hover:bg-brand-blue/[0.03]"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-red">{a.category}</span>
                <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-brand-ink group-hover:text-brand-blue">{a.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-brand-muted">{a.excerpt}</p>
                <span className="mt-5 text-xs text-brand-muted">{fmtDate(a.published)} · {a.readMins} min read</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        label="Stay informed"
        title="Subscribe to MATRIVON Insights"
        body="Independent thinking on markets, distribution, industrial intelligence and business technology — delivered without the noise."
        primary={{ to: "/contact", label: "Get in touch" }}
        secondary={{ to: "/resources", label: "MATRIVON Toolkit" }}
      />
    </div>
  );
}
